<template>
	<div id="chart__svg-container">
		<h2 class="d-none">{{ subject }} over {{ time }}</h2>
	</div>
</template>

<script>
// Process the Data and then push it to the cache in fetchData 105
import { select } from "d3-selection";
import { max, extent } from "d3-array";
import "d3-transition";
import { area, curveMonotoneX } from "d3-shape";
import { axisLeft, axisBottom } from "d3-axis";
import { scaleTime, scaleLinear } from "d3-scale";
import { getUsageLineData } from "../../../utils/apiFunctions";

export default {
	name: "LineChart",
	props: {
		// defines usage over what time
		time: {
			type: String,
			validator: (value) =>
				["all", "week", "month", "year"].indexOf(value) !== -1,
		},
		// defines what data to show
		subject: {
			type: String,
			validator: (value) =>
				["testsTaken", "testsCreated", "topicsCreated"].indexOf(value) !== -1,
		},

		topicId: {
			type: String,
			default: null,
		},
	},
	data() {
		return {
			lineData: [],
			maxValue: 0,
			dateMinMax: [],
			valueScale: null,
			timeScale: null,
			xAxis: null,
			yAxis: null,
		};
	},

	watch: {
		lineData: function(newData, oldData) {
			if (newData.length !== oldData.length) {
				//Need to update time Axis or X axis
				this.dateMinMax = extent(newData, (d) => d.date);
				this.timeScale.domain(this.dateMinMax);
				this.group
					.selectAll("#x-axis")
					.transition()
					.duration(2000)
					.call(this.xAxis);
			}
			// In all instances we will change the Value Axis or Y axis
			this.maxValue = max(newData, (d) => d.value);
			this.valueScale.domain([0, this.maxValue]);
			this.group
				.selectAll("#y-axis")
				.transition()
				.duration(2000)
				.call(this.yAxis);
			this.drawChart();
		},
	},

	methods: {
		// Utils
		getCurrentYearData(allData) {
			return allData.filter(
				(d) => d.date.getFullYear() === new Date().getFullYear()
			);
		},

		// Targets element and then recreates the Path in SVG, update globals before calling this method
		drawChart() {
			const updatedSelection = this.group
				.selectAll("#data-path")
				.data([this.lineData], (d) => d.id);

			updatedSelection
				.enter()
				.insert("path", ":first-child")
				.attr("id", "data-path")
				.merge(updatedSelection)
				.transition()
				.duration(2000)
				.attr(
					"d",
					area()
						.curve(curveMonotoneX)
						.x((d) => this.timeScale(d.date))
						.y0(this.valueScale(-2))
						.y1((d) => this.valueScale(d.value))
				)
				.attr("stroke", "#F4BE37")
				.attr("stroke-width", 2)
				.attr("fill", "url(#g1)");

			const firstValue = this.valueScale(this.lineData[0].value);
			const lastValue = this.valueScale(
				this.lineData[this.lineData.length - 1].value
			);
			this.lineToOverlap.attr(
				"d",
				`M 0 ${firstValue} V ${this.valueScale(-2)} H ${
					this.width
				} V ${lastValue} `
			);

			// Extending the tick lines
			select("#y-axis")
				.selectAll(".tick")
				.selectAll("line")
				.attr("x2", "0")
				.attr("x1", this.width)
				.attr("stroke", "#E3E3E3");
			// Removing the axis line
			this.group.selectAll(".domain").attr("opacity", "0");
			// Removing tick lines from the X Axis
			select("#x-axis")
				.selectAll(".tick")
				.selectAll("line")
				.attr("opacity", "0");
		},

		// Method expects rawData to be [...{date: `yyyy-mm-dd`, value: Number}] and
		// returns [...{id: Number, date: DateObject, value: Number}]
		processData(rawData) {
			return rawData.map((d, index) => ({
				id: index,
				date: new Date(d.date),
				value: d.value,
			}));
		},

		// Checks if the data is in the cache or an API call needs to be made and returns processed data,
		// The time parameter now is deduced to all and month as other details can be filtered from the existing data
		async fetchData(time, subject) {
			const key = subject + "_" + time;
			if (this.cache[key]) {
				return this.cache[key];
			} else {
				//Make an API call and return the
				const response = await getUsageLineData(subject, this.topicId, time);
				const data = response.data.data;
				this.cache[key] = this.processData(data);
				return this.cache[key];
			}
		},

		// Used for switching through the selection of time and subject
		async getData(time, subject) {
			if (time === "year") {
				return this.getCurrentYearData(await this.fetchData("all", subject));
			} else {
				return await this.fetchData(time, subject);
			}
		},

		async createChart() {
			this.margin = { top: 10, right: 100, bottom: 30, left: 30 };
			this.width = 1200 - this.margin.left - this.margin.right;
			this.height = 400 - this.margin.top - this.margin.bottom;
			this.cache = {};
			this.rawData = {
				testsCreated_all: [
					{ date: "2019-12-30", value: 2 },
					{ date: "2020-01-07", value: 5 },
					{ date: "2020-01-14", value: 3 },
					{ date: "2020-01-21", value: 2 },
					{ date: "2020-01-28", value: 6 },
					{ date: "2020-02-07", value: 0 },
					{ date: "2020-02-14", value: 2 },
					{ date: "2020-02-21", value: 1 },
					{ date: "2020-02-28", value: 1 },
					{ date: "2020-03-07", value: 1 },
					{ date: "2020-03-14", value: 5 },
					{ date: "2020-03-21", value: 0 },
					{ date: "2020-03-28", value: 2 },
					{ date: "2020-04-07", value: 5 },
					{ date: "2020-04-14", value: 0 },
					{ date: "2020-04-21", value: 4 },
					{ date: "2020-04-28", value: 5 },
					{ date: "2020-05-07", value: 0 },
					{ date: "2020-05-14", value: 5 },
					{ date: "2020-05-21", value: 0 },
					{ date: "2020-05-28", value: 5 },
					{ date: "2020-06-07", value: 3 },
					{ date: "2020-06-14", value: 3 },
					{ date: "2020-06-21", value: 0 },
					{ date: "2020-06-28", value: 0 },
					{ date: "2020-07-07", value: 2 },
					{ date: "2020-07-14", value: 3 },
					{ date: "2020-07-21", value: 5 },
					{ date: "2020-07-28", value: 0 },
					{ date: "2020-08-07", value: 2 },
					{ date: "2020-08-14", value: 2 },
					{ date: "2020-08-21", value: 3 },
					{ date: "2020-08-28", value: 0 },
					{ date: "2020-09-07", value: 3 },
					{ date: "2020-09-14", value: 2 },
					{ date: "2020-09-21", value: 3 },
					{ date: "2020-09-28", value: 4 },
					{ date: "2020-10-07", value: 6 },
					{ date: "2020-10-14", value: 0 },
					{ date: "2020-10-21", value: 1 },
					{ date: "2020-10-28", value: 4 },
					{ date: "2020-11-07", value: 3 },
					{ date: "2020-11-14", value: 1 },
					{ date: "2020-11-21", value: 2 },
					{ date: "2020-11-28", value: 3 },
					{ date: "2020-12-07", value: 0 },
					{ date: "2020-12-14", value: 5 },
					{ date: "2020-12-21", value: 3 },
					{ date: "2020-12-28", value: 1 },
				],
				topicsCreated_all: [
					{ date: "2019-12-30", value: 0 },
					{ date: "2020-01-07", value: 3 },
					{ date: "2020-01-14", value: 3 },
					{ date: "2020-01-21", value: 0 },
					{ date: "2020-01-28", value: 4 },
					{ date: "2020-02-07", value: 0 },
					{ date: "2020-02-14", value: 0 },
					{ date: "2020-02-21", value: 1 },
					{ date: "2020-02-28", value: 1 },
					{ date: "2020-03-07", value: 1 },
					{ date: "2020-03-14", value: 3 },
					{ date: "2020-03-21", value: 0 },
					{ date: "2020-03-28", value: 0 },
					{ date: "2020-04-07", value: 3 },
					{ date: "2020-04-14", value: 0 },
					{ date: "2020-04-21", value: 4 },
					{ date: "2020-04-28", value: 3 },
					{ date: "2020-05-07", value: 0 },
					{ date: "2020-05-14", value: 3 },
					{ date: "2020-05-21", value: 0 },
					{ date: "2020-05-28", value: 3 },
					{ date: "2020-06-07", value: 3 },
					{ date: "2020-06-14", value: 3 },
					{ date: "2020-06-21", value: 0 },
					{ date: "2020-06-28", value: 0 },
					{ date: "2020-07-07", value: 0 },
					{ date: "2020-07-14", value: 3 },
					{ date: "2020-07-21", value: 3 },
					{ date: "2020-07-28", value: 0 },
					{ date: "2020-08-07", value: 0 },
					{ date: "2020-08-14", value: 0 },
					{ date: "2020-08-21", value: 3 },
					{ date: "2020-08-28", value: 0 },
					{ date: "2020-09-07", value: 3 },
					{ date: "2020-09-14", value: 0 },
					{ date: "2020-09-21", value: 3 },
					{ date: "2020-09-28", value: 4 },
					{ date: "2020-10-07", value: 4 },
					{ date: "2020-10-14", value: 0 },
					{ date: "2020-10-21", value: 1 },
					{ date: "2020-10-28", value: 4 },
					{ date: "2020-11-07", value: 3 },
					{ date: "2020-11-14", value: 1 },
					{ date: "2020-11-21", value: 0 },
					{ date: "2020-11-28", value: 3 },
					{ date: "2020-12-07", value: 0 },
					{ date: "2020-12-14", value: 3 },
					{ date: "2020-12-21", value: 3 },
					{ date: "2020-12-28", value: 1 },
				],
				testsTaken_all: [
					{ date: "2019-12-30", value: 12 },
					{ date: "2020-01-07", value: 15 },
					{ date: "2020-01-14", value: 13 },
					{ date: "2020-01-21", value: 12 },
					{ date: "2020-01-28", value: 0 },
					{ date: "2020-02-07", value: 10 },
					{ date: "2020-02-14", value: 12 },
					{ date: "2020-02-21", value: 11 },
					{ date: "2020-02-28", value: 11 },
					{ date: "2020-03-07", value: 5 },
					{ date: "2020-03-14", value: 15 },
					{ date: "2020-03-21", value: 10 },
					{ date: "2020-03-28", value: 12 },
					{ date: "2020-04-07", value: 15 },
					{ date: "2020-04-14", value: 10 },
					{ date: "2020-04-21", value: 14 },
					{ date: "2020-04-28", value: 15 },
					{ date: "2020-05-07", value: 10 },
					{ date: "2020-05-14", value: 15 },
					{ date: "2020-05-21", value: 32 },
					{ date: "2020-05-28", value: 15 },
					{ date: "2020-06-07", value: 13 },
					{ date: "2020-06-14", value: 13 },
					{ date: "2020-06-21", value: 10 },
					{ date: "2020-06-28", value: 10 },
					{ date: "2020-07-07", value: 12 },
					{ date: "2020-07-14", value: 13 },
					{ date: "2020-07-21", value: 15 },
					{ date: "2020-07-28", value: 55 },
					{ date: "2020-08-07", value: 12 },
					{ date: "2020-08-14", value: 12 },
					{ date: "2020-08-21", value: 13 },
					{ date: "2020-08-28", value: 10 },
					{ date: "2020-09-07", value: 13 },
					{ date: "2020-09-14", value: 12 },
					{ date: "2020-09-21", value: 13 },
					{ date: "2020-09-28", value: 14 },
					{ date: "2020-10-07", value: 16 },
					{ date: "2020-10-14", value: 40 },
					{ date: "2020-10-21", value: 11 },
					{ date: "2020-10-28", value: 14 },
					{ date: "2020-11-07", value: 13 },
					{ date: "2020-11-14", value: 11 },
					{ date: "2020-11-21", value: 12 },
					{ date: "2020-11-28", value: 13 },
					{ date: "2020-12-07", value: 30 },
					{ date: "2020-12-14", value: 15 },
					{ date: "2020-12-21", value: 13 },
					{ date: "2020-12-28", value: 11 },
				],
				testsCreated_month: [
					{ date: "2020-01-01", value: 5 },
					{ date: "2020-01-02", value: 3 },
					{ date: "2020-01-03", value: 2 },
					{ date: "2020-01-04", value: 6 },
					{ date: "2020-01-05", value: 0 },
					{ date: "2020-01-06", value: 2 },
					{ date: "2020-01-07", value: 1 },
					{ date: "2020-01-08", value: 1 },
					{ date: "2020-01-09", value: 1 },
					{ date: "2020-01-10", value: 5 },
					{ date: "2020-01-11", value: 0 },
					{ date: "2020-01-12", value: 2 },
					{ date: "2020-01-13", value: 5 },
					{ date: "2020-01-14", value: 0 },
					{ date: "2020-01-15", value: 4 },
					{ date: "2020-01-16", value: 5 },
					{ date: "2020-01-17", value: 0 },
					{ date: "2020-01-18", value: 5 },
					{ date: "2020-01-19", value: 0 },
					{ date: "2020-01-20", value: 5 },
					{ date: "2020-01-21", value: 3 },
					{ date: "2020-01-22", value: 3 },
					{ date: "2020-01-23", value: 0 },
					{ date: "2020-01-24", value: 0 },
					{ date: "2020-01-25", value: 2 },
					{ date: "2020-01-26", value: 3 },
					{ date: "2020-01-27", value: 5 },
					{ date: "2020-01-28", value: 0 },
					{ date: "2020-01-29", value: 2 },
					{ date: "2020-01-30", value: 2 },
					{ date: "2020-01-31", value: 2 },
				],
				topicsCreated_month: [
					{ date: "2020-01-01", value: 0 },
					{ date: "2020-01-02", value: 0 },
					{ date: "2020-01-03", value: 0 },
					{ date: "2020-01-04", value: 0 },
					{ date: "2020-01-05", value: 0 },
					{ date: "2020-01-06", value: 2 },
					{ date: "2020-01-07", value: 1 },
					{ date: "2020-01-08", value: 1 },
					{ date: "2020-01-09", value: 1 },
					{ date: "2020-01-10", value: 5 },
					{ date: "2020-01-11", value: 0 },
					{ date: "2020-01-12", value: 2 },
					{ date: "2020-01-13", value: 5 },
					{ date: "2020-01-14", value: 0 },
					{ date: "2020-01-15", value: 4 },
					{ date: "2020-01-16", value: 5 },
					{ date: "2020-01-17", value: 0 },
					{ date: "2020-01-18", value: 5 },
					{ date: "2020-01-19", value: 0 },
					{ date: "2020-01-20", value: 5 },
					{ date: "2020-01-21", value: 3 },
					{ date: "2020-01-22", value: 3 },
					{ date: "2020-01-23", value: 0 },
					{ date: "2020-01-24", value: 0 },
					{ date: "2020-01-25", value: 2 },
					{ date: "2020-01-26", value: 0 },
					{ date: "2020-01-27", value: 0 },
					{ date: "2020-01-28", value: 0 },
					{ date: "2020-01-29", value: 0 },
					{ date: "2020-01-30", value: 0 },
					{ date: "2020-01-31", value: 0 },
				],
				testsTaken_month: [
					{ date: "2020-01-01", value: 10 },
					{ date: "2020-01-02", value: 10 },
					{ date: "2020-01-03", value: 10 },
					{ date: "2020-01-04", value: 10 },
					{ date: "2020-01-05", value: 10 },
					{ date: "2020-01-06", value: 2 },
					{ date: "2020-01-07", value: 1 },
					{ date: "2020-01-08", value: 1 },
					{ date: "2020-01-09", value: 1 },
					{ date: "2020-01-10", value: 5 },
					{ date: "2020-01-11", value: 0 },
					{ date: "2020-01-12", value: 2 },
					{ date: "2020-01-13", value: 5 },
					{ date: "2020-01-14", value: 0 },
					{ date: "2020-01-15", value: 4 },
					{ date: "2020-01-16", value: 5 },
					{ date: "2020-01-17", value: 0 },
					{ date: "2020-01-18", value: 5 },
					{ date: "2020-01-19", value: 0 },
					{ date: "2020-01-20", value: 5 },
					{ date: "2020-01-21", value: 3 },
					{ date: "2020-01-22", value: 3 },
					{ date: "2020-01-23", value: 0 },
					{ date: "2020-01-24", value: 0 },
					{ date: "2020-01-25", value: 2 },
					{ date: "2020-01-26", value: 3 },
					{ date: "2020-01-27", value: 10 },
					{ date: "2020-01-28", value: 10 },
					{ date: "2020-01-29", value: 10 },
					{ date: "2020-01-30", value: 10 },
					{ date: "2020-01-31", value: 10 },
				],
			};
			this.svg = select("#chart__svg-container")
				.append("svg")
				.attr(
					"viewBox",
					`0 0 ${this.width + this.margin.left} ${this.height +
						this.margin.top +
						this.margin.bottom}`
				);

			this.group = this.svg
				.append("g")
				.attr("transform", `translate(${this.margin.left},${this.margin.top})`);
			// Generating gradient
			const gradient = this.svg
				.append("linearGradient")
				.attr("id", "g1")
				.attr("gradientUnits", "userSpaceOnUse")
				.attr("x1", "100%")
				.attr("y1", "100%")
				.attr("x2", "100%")
				.attr("y2", "0%");
			gradient
				.append("stop")
				.attr("stop-color", "#FFFFFF")
				.attr("offset", "0")
				.attr("stop-opacity", "0");
			gradient
				.append("stop")
				.attr("stop-color", "#F8DD9C")
				.attr("offset", "1");
			const tempData = await this.getData(this.time, this.subject);

			this.maxValue = max(tempData, (d) => d.value);
			this.valueScale = scaleLinear()
				.domain([0, this.maxValue])
				.range([this.height, 0]);
			this.lineToOverlap = this.group
				.append("path")
				.attr("stroke-width", "4")
				.attr("fill", "none")
				.attr("stroke", "#fff");
			this.yAxis = axisLeft(this.valueScale);
			this.group
				.append("g")
				.attr("id", "y-axis")
				.call(this.yAxis);

			this.dateMinMax = extent(tempData, (d) => d.date);
			this.timeScale = scaleTime()
				.domain(this.dateMinMax)
				.range([0, this.width]);
			this.xAxis = axisBottom(this.timeScale);
			this.group
				.append("g")
				.attr("transform", `translate(0, ${this.height})`)
				.attr("id", "x-axis")
				.call(this.xAxis);

			this.lineData = tempData;
		},
	},

	mounted() {
		this.createChart();
	},

	async beforeUpdate() {
		// Changing the chart's data and axis, visual changes will not be reflected yet
		this.lineData = await this.getData(this.time, this.subject);
	},
};
</script>
